import i18next from "i18next";

/**
 * Van een dienst naar een agenda-afspraak.
 *
 * De vorm volgt bewust die van de ICS-feed in de api (`calendar-feed.service.ts`): wie van de ene
 * koppeling naar de andere overstapt hoort dezelfde afspraken te zien, niet een tweede dialect.
 */

/** Alleen wat een afspraak nodig heeft; de gegenereerde `Shift` past hier structureel in. */
export interface ShiftForCalendar {
  id: string;
  startAt: string;
  endAt: string;
  note?: string | null;
  project?: {
    name?: string | null;
    description?: string | null;
    addressLine?: string | null;
    zipCode?: string | null;
    city?: string | null;
  } | null;
  position?: { name?: string | null } | null;
  userFunction?: { name?: string | null } | null;
}

export interface DesiredEvent {
  shiftId: string;
  title: string;
  location: string | null;
  description: string;
  /** Epoch-milliseconden; de plugin rekent in getallen, niet in ISO-strings. */
  startDate: number;
  endDate: number;
}

const MARKER_PREFIX = "boomer:shift:";

/**
 * De handtekening waarmee we onze eigen afspraken herkennen.
 *
 * We schrijven in de privé-agenda van een ander. Zonder handtekening zouden we bij het opruimen
 * moeten gokken welke afspraak van ons was — en dan wist je een keer iemands verjaardag. Alles wat
 * we verwijderen of wijzigen draagt deze regel.
 */
export function buildMarker(shiftId: string): string {
  return `${MARKER_PREFIX}${shiftId}`;
}

/** Geeft het dienst-id terug als deze afspraak van ons is, anders `null`. */
export function parseShiftMarker(description: string | null | undefined): string | null {
  if (!description) {
    return null;
  }
  const match = new RegExp(`${MARKER_PREFIX}([^\\s]+)`).exec(description);
  return match?.[1] ?? null;
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

/**
 * De titel is het enige dat in een maandweergave overblijft — daar wordt hij afgekapt. Vandaar het
 * project vooraan (dat identificeert de klus) en de rol erachter (die onderscheidt twee diensten op
 * hetzelfde project).
 */
export function formatTitle(shift: ShiftForCalendar): string {
  const project = clean(shift.project?.name);
  const role = clean(shift.position?.name) ?? clean(shift.userFunction?.name);

  if (project && role) {
    return `${project} — ${role}`;
  }
  return project ?? role ?? i18next.t("shift:calendar.event-title");
}

export function formatLocation(shift: ShiftForCalendar): string | null {
  const parts = [
    clean(shift.project?.addressLine),
    [clean(shift.project?.zipCode), clean(shift.project?.city)].filter(Boolean).join(" "),
  ].filter((part) => part && part.length > 0);

  return parts.length > 0 ? parts.join(", ") : null;
}

export function formatDescription(shift: ShiftForCalendar): string {
  const facts: string[] = [];
  const project = clean(shift.project?.name);
  const fn = clean(shift.userFunction?.name);
  const position = clean(shift.position?.name);

  if (project) {
    facts.push(`Project: ${project}`);
  }
  if (fn) {
    facts.push(`Functie: ${fn}`);
  }
  // Alleen als de positie iets anders zegt dan de functie — anders staat er twee keer hetzelfde.
  if (position && position !== fn) {
    facts.push(`Positie: ${position}`);
  }

  const blocks = [facts.join("\n"), clean(shift.note), buildMarker(shift.id)].filter(
    (block): block is string => !!block,
  );

  return blocks.join("\n\n");
}

/**
 * `null` bij een dienst zonder bruikbare tijden. Die hoort niet in een agenda thuis, en een `NaN`
 * als begintijd maakt op iOS een afspraak in 1970.
 */
export function toDesiredEvent(shift: ShiftForCalendar): DesiredEvent | null {
  const startDate = new Date(shift.startAt).getTime();
  const endDate = new Date(shift.endAt).getTime();

  if (!Number.isFinite(startDate) || !Number.isFinite(endDate) || endDate <= startDate) {
    return null;
  }

  return {
    shiftId: shift.id,
    title: formatTitle(shift),
    location: formatLocation(shift),
    description: formatDescription(shift),
    startDate,
    endDate,
  };
}
